import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../config/config.js";

const initialForm = {
    idsport: "",
    name: "",
    acronym: "",
    image_path: "",
    gender: "",
    color: "",
    isFut: false,
    typeSport: "",
};

const CreateSportMaster = () => {
    const [showModal, setShowModal] = useState(false);
    const [sports, setSports] = useState([]);
    const [filteredSports, setFilteredSports] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [form, setForm] = useState(initialForm);
    const [selectedSport, setSelectedSport] = useState(null);
    const [mode, setMode] = useState("create"); // create | edit
    const [loadingList, setLoadingList] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");

    const openModal = async () => {
        setShowModal(true);
        setErrorMsg("");
        setSuccessMsg("");
        setForm(initialForm);
        setSelectedSport(null);
        setMode("create");
        await fetchSports();
    };

    const closeModal = () => {
        setShowModal(false);
        setForm(initialForm);
        setSelectedSport(null);
        setSearchTerm("");
        setErrorMsg("");
        setSuccessMsg("");
    };

    const fetchSports = async () => {
        try {
            setLoadingList(true);
            const { data } = await axios.get(`${API_BASE_URL}/sports`);
            setSports(data || []);
            setFilteredSports(data || []);
        } catch (error) {
            console.error("Error al cargar deportes:", error);
            setErrorMsg("No se pudieron cargar los deportes.");
        } finally {
            setLoadingList(false);
        }
    };

    // Filtro por nombre/acrónimo
    useEffect(() => {
        const term = searchTerm.toLowerCase();
        const filtered = sports.filter((sp) => {
            const name = sp.name ? sp.name.toLowerCase() : "";
            const acronym = sp.acronym ? sp.acronym.toLowerCase() : "";
            return name.includes(term) || acronym.includes(term);
        });
        setFilteredSports(filtered);
    }, [searchTerm, sports]);

    const handleSelectSport = (sp) => {
        setSelectedSport(sp);
        setMode("edit");
        setErrorMsg("");
        setSuccessMsg("");

        setForm({
            idsport: sp.idsport != null ? String(sp.idsport) : "",
            name: sp.name || "",
            acronym: sp.acronym || "",
            image_path: sp.image_path || "",
            gender:
                sp.gender === null || sp.gender === undefined ? "" : String(sp.gender),
            color: sp.color || "",
            isFut: !!sp.isFut,
            typeSport:
                sp.typeSport === null || sp.typeSport === undefined
                    ? ""
                    : String(sp.typeSport),
        });
    };


    const handleNewSport = () => {
        setSelectedSport(null);
        setMode("create");
        setErrorMsg("");
        setSuccessMsg("");
        setForm(initialForm);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === "isFut") {
            setForm((prev) => ({ ...prev, isFut: checked }));
        } else {
            setForm((prev) => ({ ...prev, [name]: value }));
        }
    };


    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErrorMsg("");
        setSuccessMsg("");

        try {
            if (!form.name || form.name.trim() === "") {
                setErrorMsg("El nombre del deporte es obligatorio.");
                setSaving(false);
                return;
            }

            // idsport opcional solo en creación
            let idsportValue = undefined;
            if (mode === "create" && form.idsport !== "") {
                const parsed = Number(form.idsport);
                if (!Number.isInteger(parsed)) {
                    setErrorMsg("El idsport debe ser un número entero.");
                    setSaving(false);
                    return;
                }
                idsportValue = parsed;
            }

            const payload = {
                ...(idsportValue !== undefined && { idsport: idsportValue }),
                name: form.name.trim(),
                acronym: form.acronym.trim() || null,
                image_path: form.image_path.trim() || null,
                gender:
                    form.gender === "" || form.gender === null
                        ? null
                        : Number.isNaN(Number(form.gender))
                            ? null
                            : Number(form.gender),
                color: form.color.trim() || null,
                isFut: form.isFut,
                typeSport:
                    form.typeSport === "" || form.typeSport === null
                        ? null
                        : Number.isNaN(Number(form.typeSport))
                            ? null
                            : Number(form.typeSport),
            };

            if (mode === "create") {
                await axios.post(`${API_BASE_URL}/sport`, payload);
                setSuccessMsg("Deporte creado correctamente.");
            } else if (mode === "edit" && selectedSport) {
                await axios.patch(`${API_BASE_URL}/sport`, {
                    idsport: selectedSport.idsport,
                    ...payload,
                });
                setSuccessMsg("Deporte actualizado correctamente.");
            }

            await fetchSports();
        } catch (error) {
            console.error("Error al guardar deporte:", error);
            setErrorMsg(
                error?.response?.data?.msg || "Ocurrió un error al guardar el deporte."
            );
        } finally {
            setSaving(false);
        }
    };


    return (
        <div className="w-full">
            {/* Card principal */}
            <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                    <h2 className="text-lg font-semibold text-gray-800">
                        Crear / Editar Deporte
                    </h2>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                    Gestiona el maestro de deportes: crea nuevos o actualiza nombre,
                    acrónimo, color y configuración general.
                </p>
                <button
                    type="button"
                    onClick={openModal}
                    className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                    Abrir gestor de deportes
                </button>
            </div>

            {/* Modal */}
            {showModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
                    onClick={closeModal}
                >
                    <div
                        className="bg-white rounded-lg shadow-xl w-full max-w-5xl mx-4 p-6 max-h-[90vh] overflow-y-auto"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-xl font-semibold text-gray-800">
                                {mode === "create"
                                    ? "Crear nuevo deporte"
                                    : `Editar deporte${selectedSport
                                        ? `: ${selectedSport.acronym || selectedSport.name}`
                                        : ""
                                    }`}
                            </h3>
                            <button
                                type="button"
                                onClick={closeModal}
                                className="text-gray-500 hover:text-gray-700 text-xl leading-none"
                            >
                                ×
                            </button>
                        </div>

                        {errorMsg && (
                            <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">
                                {errorMsg}
                            </div>
                        )}
                        {successMsg && (
                            <div className="mb-3 text-sm text-green-700 bg-green-50 border border-green-200 px-3 py-2 rounded">
                                {successMsg}
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Lista de deportes */}
                            <div className="md:col-span-1 border border-gray-200 rounded-lg p-3">
                                <div className="flex items-center justify-between mb-2">
                                    <h4 className="text-sm font-semibold text-gray-800">
                                        Buscar deporte
                                    </h4>
                                    <button
                                        type="button"
                                        onClick={handleNewSport}
                                        className="text-xs px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-800"
                                    >
                                        Nuevo
                                    </button>
                                </div>

                                <input
                                    type="text"
                                    placeholder="Buscar por nombre o acrónimo..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full mb-3 px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />

                                <div className="max-h-72 overflow-y-auto border border-gray-100 rounded">
                                    {loadingList ? (
                                        <div className="p-3 text-sm text-gray-500">
                                            Cargando deportes...
                                        </div>
                                    ) : filteredSports.length === 0 ? (
                                        <div className="p-3 text-sm text-gray-500">
                                            No se encontraron deportes.
                                        </div>
                                    ) : (
                                        filteredSports.map((sp) => (
                                            <button
                                                key={sp.idsport}
                                                type="button"
                                                onClick={() => handleSelectSport(sp)}
                                                className={`w-full text-left px-3 py-2 text-sm border-b border-gray-100 hover:bg-blue-50 transition-colors ${selectedSport && selectedSport.idsport === sp.idsport
                                                    ? "bg-blue-100"
                                                    : ""
                                                    }`}
                                            >
                                                <div className="font-medium text-gray-800">
                                                    {sp.acronym || "Sin acrónimo"}
                                                </div>
                                                <div className="text-xs text-gray-500 truncate">
                                                    {sp.name}
                                                </div>
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Formulario */}
                            <div className="md:col-span-2 border border-gray-200 rounded-lg p-4">
                                <form onSubmit={handleSave} className="space-y-3">
                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                            ID Deporte (idsport)
                                        </label>
                                        <input
                                            type="number"
                                            name="idsport"
                                            value={form.idsport}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                                            placeholder="Opcional: déjalo vacío para autoincremental"
                                            disabled={mode === "edit"} // en edición solo lectura
                                        />
                                        <p className="mt-1 text-[11px] text-gray-500">
                                            Si lo dejas vacío, el sistema asignará el ID automáticamente.
                                        </p>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Nombre (name) *
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={form.name}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Ej: Fútbol Varones"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Acrónimo (acronym)
                                        </label>
                                        <input
                                            type="text"
                                            name="acronym"
                                            value={form.acronym}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Ej: FTV, FUTV"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-medium text-gray-700 mb-1">
                                            Ruta de imagen (image_path)
                                        </label>
                                        <input
                                            type="text"
                                            name="image_path"
                                            value={form.image_path}
                                            onChange={handleChange}
                                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            placeholder="Ej: deportes/futbol.png"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Género (gender)
                                            </label>
                                            <select
                                                name="gender"
                                                value={form.gender}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                                <option value="">Sin especificar</option>
                                                <option value="1">Masculino</option>
                                                <option value="2">Femenino</option>
                                                <option value="3">Mixto</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Color (color)
                                            </label>
                                            <input
                                                type="text"
                                                name="color"
                                                value={form.color}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Ej: #1976D2"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-center">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Tipo de deporte (typeSport)
                                            </label>
                                            <input
                                                type="number"
                                                name="typeSport"
                                                value={form.typeSport}
                                                onChange={handleChange}
                                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Ej: 1, 2..."
                                            />
                                            <p className="mt-1 text-[11px] text-gray-500">
                                                Usa el código que manejes en tu sistema (colectivo,
                                                individual, etc.).
                                            </p>
                                        </div>

                                        <div className="flex items-center mt-2">
                                            <input
                                                id="isFut"
                                                type="checkbox"
                                                name="isFut"
                                                checked={form.isFut}
                                                onChange={handleChange}
                                                className="mr-2"
                                            />
                                            <label
                                                htmlFor="isFut"
                                                className="text-xs font-medium text-gray-700"
                                            >
                                                Es fútbol / usa lógica especial (isFut)
                                            </label>
                                        </div>
                                    </div>

                                    <div className="pt-2 flex justify-end space-x-2">
                                        <button
                                            type="button"
                                            onClick={closeModal}
                                            className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={saving}
                                            className="px-4 py-2 text-sm rounded bg-blue-600 text-white font-medium hover:bg-blue-700 disabled:opacity-60"
                                        >
                                            {saving
                                                ? "Guardando..."
                                                : mode === "create"
                                                    ? "Crear deporte"
                                                    : "Guardar cambios"}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreateSportMaster;
